import {
    GridCellParams,
    GridColumnOrderChangeParams,
    GridRowGroupingModel,
    GridSortModel,
    GridColumnVisibilityModel,
    GridColumnResizeParams,
    GRID_CHECKBOX_SELECTION_FIELD,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    MuiEvent,
    GridPinnedColumns,
    useGridApiRef,
} from '@enterprise/material-data-grid';
import { MouseEvent, useMemo } from 'react';
import { CombinedItemGridState, CombinedItemState } from '../../../../../store';
import { ItemsGridColumn } from '../../../items/itemsGrid';

export enum CombinedItemDetailsGridColumn {
    // todo: will be used if we decide to made mobile view for this grid
    Id = 'pimsId',
    Description = 'hospitalDescription',
    Order = 'order',
    Practice = 'provider.siteId',
    ItemType = 'itemType.id',
    SellingPrice = 'prices.listPrice',
    NonChargeable = 'nonChargeable',
    Quantity = 'quantity',
}

export interface UseCombinedItemDetailPanelParams {
    state?: CombinedItemState;
    onStateChange?: (state: CombinedItemState) => void;
}

export const useCombinedItemDetailPanel = (params: UseCombinedItemDetailPanelParams) => {
    const { state, onStateChange } = params;
    const gridState = state?.grid;

    const apiRef = useGridApiRef();

    const columnSizes = useMemo(
        () =>
            new Map<CombinedItemDetailsGridColumn, number>([
                [CombinedItemDetailsGridColumn.Id, 150],
                [CombinedItemDetailsGridColumn.Description, 442],
                [CombinedItemDetailsGridColumn.Order, 115],
                [CombinedItemDetailsGridColumn.Practice, 200],
                [CombinedItemDetailsGridColumn.ItemType, 110],
                [CombinedItemDetailsGridColumn.SellingPrice, 130],
                [CombinedItemDetailsGridColumn.NonChargeable, 210],
                [CombinedItemDetailsGridColumn.Quantity, 90],
            ]),
        [],
    );

    const updateState = (newState: Partial<CombinedItemGridState>) => {
        onStateChange?.({
            ...(newState || {}),
            grid: {
                ...gridState,
                ...newState,
            },
        });
    };

    const pinnedColumns: GridPinnedColumns = useMemo(
        () => ({
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, ...(gridState?.pinned?.left || [])],
            right: gridState?.pinned?.right,
        }),
        [JSON.stringify(gridState?.pinned)],
    );

    const onPinnedColumnsChange = (pinnedColumns: GridPinnedColumns) => {
        updateState({
            pinned: {
                left: pinnedColumns.left?.filter((item) => item !== GRID_CHECKBOX_SELECTION_COL_DEF.field),
                right: pinnedColumns.right,
            },
        });
    };

    const onCellClick = (params: GridCellParams, event: MuiEvent<MouseEvent>) => {
        if (params.field === GRID_CHECKBOX_SELECTION_FIELD) {
            return;
        }

        event.preventDefault();
    };

    const onColumnWidthChange = ({ colDef, width }: GridColumnResizeParams) => {
        const field = [...columnSizes.keys()].find((item) => colDef.field.includes(item));

        if (width !== undefined && field && columnSizes.get(field) === width) {
            return;
        }

        const model = gridState?.widthModel || {};
        model[colDef.field] = width;

        updateState({
            widthModel: model,
        });
    };

    const columnVisibilityModel: GridColumnVisibilityModel | undefined = useMemo(() => {
        if (!apiRef.current) {
            return;
        }

        if (!gridState?.visibilityModel) {
            return {};
        }

        const model = Object.entries(gridState?.visibilityModel).reduce((agg, [key, value]) => {
            const isVisible = !gridState?.groupingModel?.includes(key as ItemsGridColumn) && value;

            return {
                ...agg,
                [key]: isVisible,
            };
        }, {});

        return model;
    }, [JSON.stringify(gridState?.visibilityModel), gridState?.groupingModel, apiRef.current]);

    const onColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
        const visibilityModel: Record<number | string, boolean | undefined> = { ...model };

        Object.entries(visibilityModel).forEach(([key]) => {
            if (gridState?.groupingModel?.includes(key)) {
                visibilityModel[key] = gridState?.visibilityModel?.[key];
            }
        });

        updateState({
            visibilityModel,
        });
    };

    const sortModel: GridSortModel | undefined = useMemo(() => {
        if (!apiRef.current) {
            return;
        }

        if (!gridState?.sortModel) {
            return [];
        }

        return gridState?.sortModel;
    }, [gridState?.sortModel, apiRef.current]);

    const onSortModelChange = (model: GridSortModel) => {
        updateState({
            sortModel: model,
        });
    };

    const rowGroupingModel = useMemo(() => {
        return gridState?.groupingModel;
    }, [String(gridState?.groupingModel)]);

    const onRowGroupingModelChange = (model: GridRowGroupingModel | undefined) => {
        const removed = gridState?.groupingModel?.filter((name) => !model?.includes(name)) || [];
        const added = model?.filter((name) => !gridState?.groupingModel?.includes(name)) || [];
        const visibilityModel = gridState?.visibilityModel || {};

        updateState({
            groupingModel: model,
            visibilityModel: {
                ...visibilityModel,
                ...removed.reduce((agg, name) => ({ ...agg, [name]: true }), {}),
                ...added.reduce((agg, name) => ({ ...agg, [name]: false }), {}),
            },
        });
    };

    const onColumnOrderChange = (params: GridColumnOrderChangeParams) => {
        updateState({
            orderModel: {
                ...(gridState?.orderModel || {}),
                [params.column.field]: params.targetIndex,
            },
        });
    };

    return {
        apiRef,
        columnSizes,
        pinnedColumns,
        onPinnedColumnsChange,
        onCellClick,
        onColumnWidthChange,
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        sortModel,
        onSortModelChange,
        rowGroupingModel,
        onRowGroupingModelChange,
        onColumnOrderChange,
    };
};
